
import { CameraPropertiesFluid, Engine, Configurator, AssetProvider, AssetProviderMode, AssetInfoTypes, CameraTransitionProperties, IOrbitCameraProperties, IEnvironmentInfo } from "@viscircle-org/threngine";
import { DirectionalLight, Vector3, Scene } from "three";


export interface MoveCameraToOverwriteProps {
    orbit?:Partial<IOrbitCameraProperties>
    transition?:Partial<CameraTransitionProperties>
}

const DEFAULT_CAMERA_POSITION = [60, 60, 50];
const DEFAULT_CAMERA_TARGET = [20, 6, -25];

export class RenderEngine {
    private _scene:Scene;
    public engine:Engine;

    constructor() {
    }

    public init = async (mount:HTMLDivElement):Promise<void> => {
        const MODE: string = AssetProviderMode.Dev;//AssetProviderMode.Staging : AssetProviderMode.Live;
        const server = "https://srv.3dcommerce.studio";
        const project_id = "62603b32bb971f0017aa74ac";
        //const project_id = "627e6f8747e30b0019485306";
        const assetProvider = new AssetProvider({ server, project_id, mode: MODE });
        const engine = new Engine(assetProvider);

        await engine.initialize(mount);

        const assets = assetProvider.getAssets();
        const { render } = engine.services;

        await render.fadeOut(0, "white");


        const config = assets.filter(a => a.infoType === AssetInfoTypes.Scene);
        let configAsset = config[2];

        console.error("start loading");
        const configurator = new Configurator(/*config*/configAsset.info.data, engine);
        this._scene = await configurator.initialize();
        console.error("finished loading");

        this.scene.traverse(o => {
            o.receiveShadow = true;
            o.castShadow = true;
        });

        this.scene.traverse(o=>{
            if(o.name==="Sun"){
                const sun=<DirectionalLight>o.children[0];
                console.error("SUN: ", sun);
                this.intitializeSunShadows(engine, sun);
            }
        });

        //deactivate lights
        this.scene.traverse(o => {
            if (o.name.toLowerCase().includes("light")) {
                o.visible = false;
            }
        });

        //find mesaurement
        this.scene.traverse(o => {
            if (o.name.toLowerCase().includes("measurement")) {
                console.error("Found mesaurement!: ", o);
            }
        });

        // disable automatic transform updates
        // this helps to dramatically reduce CPU usage
        this.scene.updateMatrixWorld();
        this.scene.autoUpdate = false;

        // engine.services.render.renderController.renderer.sortObjects = false;

        this.engine = engine;
        await render.setScene(this.scene);

        this._scene.traverse((el) => {
            if (el.type === "PointLight") {
                let light = el as THREE.PointLight;
                light.userData.intensity = light.intensity;
                light.userData.isOn = false;
            }
        })

        await render.fadeIn(500, "white");

        // this.engine.services.render.settings.antialias.webgl1.temporal.antighostMotionOnly = false;
        // this.engine.services.render.settings.antialias.webgl2.temporal.antighostMotionOnly = false;

        this.engine.services.render.adaptiveQualityController.enabled = true;

        return Promise.resolve();
    }



    get scene(): Scene {
        return this._scene;
    }

    intitializeSunShadows(engine:Engine, sun:DirectionalLight):void {
        sun.shadow.camera.left = -100;
        sun.shadow.camera.right = 100
        sun.shadow.camera.top = 50;
        sun.shadow.camera.bottom = -50;
        sun.shadow.camera.updateProjectionMatrix();
        sun.updateMatrix();
        sun.updateMatrixWorld();
        sun.target.updateMatrixWorld();

        sun.position.set(-20, 100, 0);
        sun.intensity = 10;

        // sun.shadow.mapSize.set(2048, 2048);
        sun.shadow.mapSize.set(4096, 4096);

        const frustumL = sun.shadow.camera.left;
        const frustumR = sun.shadow.camera.right;
        const frustumT = sun.shadow.camera.top;
        const frustumB = sun.shadow.camera.bottom;
        const frustumN = sun.shadow.camera.near;
        const frustumF = sun.shadow.camera.far;
        
        // Temporally filtered shadows
        engine.services.render.onBeforeRender.subscribe(async () => {
            const randomX = (Math.random() * 2 - 1) * 0.01;
            const randomY = (Math.random() * 2 - 1) * 0.01;
            const randomZ = (Math.random() * 2 - 1) * 0.01;

            sun.shadow.camera.left = frustumL + randomX;
            sun.shadow.camera.right = frustumR + randomX;
            sun.shadow.camera.top = frustumT + randomY;
            sun.shadow.camera.bottom = frustumB + randomY;
            sun.shadow.camera.near = frustumN + randomZ;
            sun.shadow.camera.far = frustumF + randomZ;
            sun.shadow.camera.updateProjectionMatrix();
        });
    }

    public moveCameraTo = (newPos:Vector3, rot:Vector3, level:number, roundTour:boolean, callback:(reason?:string)=>void, overwriteProps:MoveCameraToOverwriteProps) => {
        console.log("RenderEngine.moveCameraTo() called!");

        //this.engine.services.render.cameraController.cameraProperties.distance_max=999999;
        this.engine.services.render.cameraController.cameraProperties.distance_min = 0

        const props = {} as CameraPropertiesFluid;
        const transitionProps = {} as CameraTransitionProperties;
        //transProps.onFinished=callback;
        const currPos = this.engine.services.render.camera.position;
        //props.position=[currPos.x,currPos.y, currPos.z];
        //props.target=[newPos.x,newPos.y, newPos.z];
        props.position = [newPos.x, newPos.y, newPos.z];
        props.target = [newPos.x + 0.1, newPos.y, newPos.z];
        props.target = props.position;
        const target = new Vector3();
        this.engine.services.render.camera.getWorldDirection(target);
        target.multiplyScalar(0.01).add(newPos);
        props.target = [target.x, target.y, target.z]

        const orbitProps = this.engine.services.render.cameraController.cameraProperties;
        orbitProps.position = props.position;
        orbitProps.target = props.target;
        transitionProps.onFinished = callback;

        
        const propsByLevel = this.propsByLevel(newPos, rot, level, roundTour);
        orbitProps.position = propsByLevel.position;
        orbitProps.target = propsByLevel.target;
        orbitProps.clip_near = propsByLevel.clipping.near;
        orbitProps.clip_far = propsByLevel.clipping.far;
        orbitProps.fov = propsByLevel.fov;
        orbitProps.limits= propsByLevel.limits;
        orbitProps.distance_min = propsByLevel.distance_min;
        orbitProps.distance_max = propsByLevel.distance_max;
        orbitProps.axis_inversion = propsByLevel.axis_inversion;
        orbitProps.speed_rotate = propsByLevel.speed_rotate;
        orbitProps.damping = propsByLevel.damping;
        
        transitionProps.time = propsByLevel.transitionTime;
        transitionProps.easing = propsByLevel.transitionEasing;

        if (overwriteProps.orbit) Object.assign(orbitProps, overwriteProps.orbit);
        if (overwriteProps.transition) Object.assign(transitionProps, overwriteProps.transition);

        //DEBUG: FREE CAMERA
        // orbitProps.limits.up = 90;
        // orbitProps.limits.down = -90;
        // orbitProps.distance_min = 0;
        // orbitProps.distance_max = 100;
        // orbitProps.disable_pan = false;
        // orbitProps.speed_pan = 0.5;
        // if (level === 4) {
        //     orbitProps.target = [orbitProps.target[0] * 1.1, orbitProps.target[1], orbitProps.target[2] * 1.1];
        // }
        //---

        console.log("overwriteProps", overwriteProps);
        
        console.log("orbitProps", orbitProps);
        console.log("transitionProps", transitionProps);
        
        this.engine.services.render.cameraController.setOrbitCamera(orbitProps, transitionProps);
    }

    propsByLevel(pos:Vector3, rot:Vector3, level:number, roundTour:boolean) {
        console.error("LEVEL: ", level);
        const clipping = { near: 3.957, far: 408.9 }
        let position = [0, 0, 0];
        let target = [0, 0, 0];
        const camDir = new Vector3();
        this.engine.services.render.camera.getWorldDirection(camDir);
        let distance = 0;
        let fov = 57.6
        let limits = {'up': -15, 'down': -90, 'left': 0, 'right': 360};
		let rot_z = rot.z;
		let distance_min = 70;
		let distance_max = 100;
        let speed_rotate = 0.07;
        let damping = 0.12;
        let axis_inversion = {mouse_horizontal: false, mouse_vertical: false, touch_horizontal: false, touch_vertical: false };
        let transitionTime = 900;
        let transitionEasing = "linear";

        switch (level) {
            case 0: { //select building
                if (roundTour) {
                    //TODO: IDEA: Fixed walking speed => transition time based on distance to target... ?
                    target = pos.toArray();

                    let camPos = new Vector3();
                    let direction = new Vector3();
                    let positionVector = new Vector3();
                    this.engine.services.render.camera.getWorldPosition(camPos);
                    camPos.setY(pos.y);
                    direction.subVectors(camPos, pos).normalize().multiplyScalar(0.00001);

                    positionVector.addVectors(pos, direction);
                    position = positionVector.toArray();

                    fov = 75;
                    limits.down = -30;
                    limits.up = 30;
                    clipping.near = 0.1;
                    clipping.far = 350;
                    distance_min = 0.001;
                    distance_max = 0.002;
                    speed_rotate = 0.06;
                    damping = 0.25;
                    axis_inversion = {mouse_horizontal: true, mouse_vertical: true, touch_horizontal: true, touch_vertical: true };
                } else {
                    target = DEFAULT_CAMERA_TARGET;
                    position = DEFAULT_CAMERA_POSITION;
                    
                    clipping.near = 0.1;
                    limits.down = -90;
                }
                break;
            }
            case 1: { //select level from building
                target = pos.toArray();
                target[1] = 0;
                distance = 25;
                rot.y += 1;
                position = rot.normalize().multiplyScalar(distance).add(pos).add(new Vector3(0, 0, 0)).toArray();
                //Down and up appear to be reversed here... ?
                limits.down = -70;
                limits.up = -40;
                limits.left = -25 + rot_z*90 + 90;
                limits.right = 25 + rot_z*90 + 90;
                distance_min = 20;
                distance_max = 30;
                break;
            }
            case 2: { //select apartment from level
                target = pos.toArray();
                distance = 25;
                rot.y += 1.5;
                position = rot.normalize().multiplyScalar(distance).add(pos).add(new Vector3(0, 0, 0)).toArray();
                //Down and up appear to be reversed here... ?
                limits.down = -90;
                limits.up = -25;
                limits.left = -25 + rot_z*90 + 90;
                limits.right = 25 + rot_z*90 + 90;
                distance_min = 20;
                distance_max = 30;
                break;
            }
            case 3: { //single apartment birds-eye view
                target = pos.toArray();
                distance = 22;
                rot.y += 1.5;
                position = rot.normalize().multiplyScalar(distance).add(pos).add(new Vector3(0, 0, 0)).toArray();
                //Down and up appear to be reversed here... ?
                limits.down = -90;
				limits.up = -45;
				limits.left = -25 + rot_z*90 + 90;
				limits.right = 25 + rot_z*90 + 90;
				distance_min = 20;
				distance_max = 30;

                transitionTime = 400;
                break;
            }
            case 4: { //select Hotspot in Flat
                target = pos.toArray();

                let camPos = new Vector3();
                let direction = new Vector3();
                let positionVector = new Vector3();
                this.engine.services.render.camera.getWorldPosition(camPos);
                camPos.setY(pos.y);
                direction.subVectors(camPos, pos).normalize().multiplyScalar(0.00001);

                positionVector.addVectors(pos, direction);
                position = positionVector.toArray();

                fov = 75;
                limits.down = -30;
				limits.up = 30;
                clipping.near = 0.1;
                clipping.far = 350;
				distance_min = 0.001;
				distance_max = 0.002;
                speed_rotate = 0.06;
                damping = 0.25;
                axis_inversion = {mouse_horizontal: true, mouse_vertical: true, touch_horizontal: true, touch_vertical: true };
                break;
            }
            default: {
                break;
            }
        }

        if (limits.left < 0) {
            limits.left += 360;
        }

        return { 
            position: position, 
            target: target, clipping: 
            clipping, fov: fov, limits: 
            limits , distance_min: 
            distance_min, distance_max: 
            distance_max, speed_rotate: 
            speed_rotate, 
            damping: damping,
            axis_inversion: axis_inversion,
            transitionTime: transitionTime, 
            transitionEasing: transitionEasing
        };
    }
}
